<ui-page class="grid h-full min-h-0" [testHook]="testHooks.Page">
	<ui-content-header
		type="page"
		title="LTT &amp; Considerations"
		[testHook]="testHooks.ContentHeader"
	>
		<ui-button
			title-action
			preset="EXTRA_LOW_EMPHASIS"
			icon="edit"
			(click)="vm.openSettings()"
			[label]="'Edit Finance Settings'"
			[testHook]="testHooks.EditFinanceSettingsButton"
		/>
	</ui-content-header>
	<div class="page-content">
		<div class="col-span-7 min-h-0 overflow-y-auto h-full">
			<ui-section
				class="mb-5"
				title="Details"
				[testHook]="testHooks.DetailsSection"
			>
				<div section-content>
					<div
						class="grid grid-cols-1 text-gray-700 items-center 2xl:grid-cols-2 gap-4"
					>
						<!-- Top two dropdowns -->
						<app-dropdown
							[itemGroups]="vm.affidavitItems"
							[ngModel]="vm.FriendlyLTTExecutedBy[vm.ltt()?.executedBy]"
							(onSelect)="vm.onSelectExecutedBy($event)"
							label="LTT Affidavit executed by"
							[testHook]="testHooks.LttAffidavitExecutedByDropdown"
						></app-dropdown>
						<app-dropdown
							[itemGroups]="vm.rateItems"
							[ngModel]="vm.FriendlyLTTRate[vm.ltt()?.rate]"
							(onSelect)="vm.setRate($event)"
							label="LTT rate based on"
							[testHook]="testHooks.LttRateBasedOnDropdown"
						></app-dropdown>

						<ng-container *ngIf="vm.ltt()?.rate === 'apportion'">
							<app-input
								(debounce)="vm.setResidenceValue($event)"
								label="Value of single family residence"
								[ngModel]="vm.ltt()?.residenceValue"
								[startIcon]="vm.ICON.DOLLAR"
								type="cents"
								[testHook]="testHooks.ValueOfSingleFamilyResidence"
							></app-input>
							<app-input
								(debounce)="vm.setLandUse($event)"
								label="Rest of the land used for"
								[ngModel]="vm.ltt()?.landUse"
								type="string"
								[testHook]="testHooks.RestOfTheLandUsedFor"
							></app-input>
						</ng-container>

						<!-- Monies paid or to be paid in cash -->
						<p class="mb-3 mt-2">Monies paid or to be paid in cash</p>
						<div class="mt-2">
							<atomic-lockable-input
								class="w-full"
								[overrideValue]="vm.ltt()?.moniesPaid"
								[dynamicValue]="vm.lttCalculated()?.nsp"
								[placeholder]="vm.lttCalculated()?.nsp | centsToDollars"
								inputType="cents"
								overrideLabelText="Override NSP"
								dynamicLabelText="Set to NSP"
								(debounce)="vm.setMoniesPaid($event)"
								(modelChange)="vm.setMoniesPaid($event)"
								[testHook]="testHooks.LTTCalculationLockableInput"
							></atomic-lockable-input>
							<app-anchor
								class="text-green cursor-pointer"
								(click)="vm.editSalePrice()"
								[testHook]="testHooks.EditSalePriceAdjustmentLink"
								>Edit sale price adjustment</app-anchor
							>
						</div>

						<!-- Assumed mortgages -->
						<p>Assumed mortgages</p>
						<app-input
							(debounce)="vm.setAssumedMortgage($event)"
							[ngModel]="vm.ltt()?.assumedMortgage"
							[startIcon]="vm.ICON.DOLLAR"
							type="cents"
							[testHook]="testHooks.AssumedMortgageInput"
						></app-input>

						<!-- Mortgages given back to vendor -->
						<p>Mortgages given back to vendor</p>
						<atomic-lockable-input
							class="w-full"
							[overrideValue]="vm.ltt()?.returnedToVendor"
							[dynamicValue]="vm.lttCalculated()?.returnedToVendor"
							[placeholder]="
								vm.lttCalculated()?.returnedToVendor | centsToDollars
							"
							inputType="cents"
							overrideLabelText="Override mortgage given back to vendor"
							dynamicLabelText="Set to mortgage given back to vendor"
							(debounce)="vm.setReturnedToVendor($event)"
							(modelChange)="vm.setReturnedToVendor($event)"
							[testHook]="testHooks.ReturnedToVendorLockableInput"
						></atomic-lockable-input>

						<!-- Property transferred in exchange -->
						<p>Property transferred in exchange</p>
						<app-input
							(debounce)="vm.setPropertyTransferred($event)"
							[ngModel]="vm.ltt()?.propertyTransferred"
							[startIcon]="vm.ICON.DOLLAR"
							type="cents"
							[testHook]="testHooks.PropertyTransferred"
						></app-input>

						<!-- Other considerations subject to tax -->
						<p>Other considerations subject to tax</p>
						<app-input
							(debounce)="vm.setConsiderations($event)"
							[ngModel]="vm.ltt()?.considerations"
							[startIcon]="vm.ICON.DOLLAR"
							type="cents"
							[testHook]="testHooks.ConsiderationssSubjecttoTax"
						></app-input>

						<!-- Chattels and other considerations -->
						<app-checkbox
							label="Apply chattels and other considerations"
							type="clear"
							[ngModel]="vm.ltt()?.applyChattels"
							(ngModelChange)="vm.setApplyChattels($event)"
							class="col-span-2"
							[testHook]="testHooks.ApplyChattelsAndOtherConsiderationsCheckbox"
						></app-checkbox>

						<ng-container *ngIf="vm.ltt()?.applyChattels">
							<app-input
								(debounce)="vm.setChattelsValue($event)"
								label="Value of all chattels"
								[ngModel]="vm.ltt()?.chattelsValue"
								type="cents"
								[testHook]="testHooks.ValueOfAllChattels"
							></app-input>
							<app-input
								(debounce)="vm.setChattelConsiderations($event)"
								label="Other considerations"
								[ngModel]="vm.ltt()?.chattelsConsiderations"
								type="cents"
								[testHook]="testHooks.OtherConsiderations"
							></app-input>
						</ng-container>

						<p>Chattels and other considerations</p>
						<p class="text-right">
							{{ vm.lttCalculated()?.chattelsConsiderations | centsToDollars }}
						</p>

						<!-- Values subject to LTT -->
						<p class="font-bold">Values subject to LTT</p>
						<p
							class="font-bold text-right"
							[testHook]="testHooks.ValuesSubjectToLTT"
						>
							{{ vm.lttCalculated()?.subjectToLtt | centsToDollars }}
						</p>

						<!-- First Time Home Buyers -->
						<p class="mt-2 col-span-2 text-gray-950 h4">
							First Time Home Buyers
						</p>

						<ui-note
							*ngIf="vm.numberOfFirstTimeHomeBuyers() > 0"
							class="col-span-2 -mb-4"
							title="First Time Home Buyer on Matter"
							type="warning"
							[testHook]="testHooks.FirstTimeHomeBuyerOnMatterNote"
						>
							<p class="p-sm">
								{{ vm.numberOfFirstTimeHomeBuyers() }} clients are selected as
								first time home buyer. Please select the Apply first time home
								buyer refund checkmark below and add the percentage of refund.
							</p>
						</ui-note>

						<app-checkbox
							type="clear"
							class="col-span-2"
							[ngModel]="vm.ltt()?.applyLTTRefund"
							(ngModelChange)="vm.setApplyLttRefund($event)"
							label="Apply first time home buyer refund"
							[testHook]="testHooks.ApplyFirstTimeHomeBuyerRefundCheckbox"
						></app-checkbox>

						<ng-container *ngIf="vm.ltt()?.applyLTTRefund" [@openClose]>
							<p>Percentage claimed</p>
							<app-input
								type="number"
								[startIcon]="vm.ICON.PERCENTAGE"
								[ngModel]="vm.ltt()?.refundPercentageClaimed"
								(ngModelChange)="vm.setRefundPercentageClaimed($event)"
								[testHook]="testHooks.PercentageClaimed"
							></app-input>
						</ng-container>

						<!-- MLTT -->
						<p class="mt-2 col-span-2 text-gray-950 h4">MLTT</p>
						<app-checkbox
							type="clear"
							class="col-span-2"
							[ngModel]="vm.ltt()?.mltt?.applied"
							(ngModelChange)="vm.setMlttApplied($event)"
							label="Apply MLTT calculations"
							[testHook]="testHooks.ApplyMlttCalculationsCheckbox"
						></app-checkbox>
						<ui-note
							class="col-span-2"
							*ngIf="vm.showLroIsOtherMessage()"
							type="error"
							icon="alert-triangle"
							title="LRO on Matter Does not Match"
							[testHook]="testHooks.LroOnMatterDoesNotMatchNote"
						>
							<p class="p-sm text-gray-700">
								Please select Toronto as the LRO before applying MLTT
							</p>
							<app-anchor
								(click)="vm.goToPropertyDetails()"
								class="text-green cursor-pointer"
								[testHook]="testHooks.GoToPropertyDetailsLink"
							>
								<div class="flex items-center gap-2">
									<span
										class="underline"
										(click)="vm.goToPropertyDetails()"
										[testHook]="testHooks.VmGoToPropertyDetailsSpan"
										>Update LRO</span
									>
									<i-tabler name="chevron-right" class="w-4 h-4"></i-tabler>
								</div>
							</app-anchor>
						</ui-note>

						<ng-container *ngIf="vm.ltt()?.mltt?.applied" [@openClose]>
							<p>MLTT admin fee</p>
							<app-input
								type="cents"
								[ngModel]="vm.ltt()?.mltt?.adminFee"
								(ngModelChange)="vm.setMlttAdminFee($event)"
								[testHook]="testHooks.MLTTAdminFeeInput"
							></app-input>

							<app-checkbox
								type="clear"
								[ngModel]="vm.ltt()?.mltt?.applyHST"
								(ngModelChange)="vm.setMlttApplyHst($event)"
								label="Apply HST ({{ vm.lttCalculated()?.HST * 100 }}%)"
								[testHook]="testHooks.ApplyHSTCheckbox"
							></app-checkbox>
							<p class="text-right">
								{{
									vm.lttCalculated()?.mlttFeeTotal - vm.ltt()?.mltt?.adminFee
										| centsToDollars
								}}
							</p>

							<p class="font-bold">Total</p>
							<p class="font-bold text-right">
								{{ vm.lttCalculated()?.mlttFeeTotal | centsToDollars }}
							</p>
						</ng-container>
					</div>
				</div>
			</ui-section>
			<ui-section
				title="Non-Resident Speculation Tax"
				[testHook]="testHooks.NonResidentSpeculationTaxSection"
			>
				<div section-content>
					<div
						class="grid grid-cols-1 text-gray-700 items-center 2xl:grid-cols-2 gap-4"
					>
						<app-checkbox
							label="Apply NRST (Non-Resident Speculation Tax)"
							type="clear"
							class="col-span-2"
							[ngModel]="vm.ltt()?.applyNRST"
							(ngModelChange)="vm.setApplyNRST($event)"
							[testHook]="testHooks.ApplyNRSTCheckbox"
						></app-checkbox>
						<ng-container *ngIf="vm.ltt()?.applyNRST">
							<app-input
								(debounce)="vm.setNrstPercentageClaimed($event)"
								label="Percentage Applied"
								[ngModel]="vm.ltt()?.nrstPercentageClaimed"
								[startIcon]="vm.ICON.PERCENTAGE"
								type="number"
								[testHook]="testHooks.PercentageApplied"
							></app-input>
							<app-input
								(debounce)="vm.setNrstReceiptNumber($event)"
								label="NRST Remittance Receipt Number"
								[ngModel]="vm.ltt()?.nrstReceiptNumber"
								type="string"
								[testHook]="testHooks.NrstRemittanceReceiptNumber"
							></app-input>

							<p class="font-bold">Non-resident speculation tax</p>
							<p class="font-bold text-right" [testHook]="testHooks.NRSTAmount">
								{{ vm.lttCalculated()?.nrst | centsToDollars }}
							</p>

							<p class="h4 text-gray-950 mt-2">MNRST</p>
							<app-checkbox
								label="Apply MNRST (Municipal Non-Resident Speculation Tax)"
								type="clear"
								class="col-span-2"
								[ngModel]="vm.ltt()?.applyMNRST"
								(ngModelChange)="vm.setMlttApplyMnrst($event)"
								[testHook]="testHooks.ApplyMNRSTCheckbox"
							></app-checkbox>
							<ng-container *ngIf="vm.ltt()?.applyMNRST">
								<app-input
									(debounce)="vm.setMnrstPercentageClaimed($event)"
									label="Percentage Applied"
									[ngModel]="vm.ltt()?.mnrstPercentageClaimed"
									[startIcon]="vm.ICON.PERCENTAGE"
									type="number"
									[testHook]="testHooks.SetMRSTPercentageApplied"
								></app-input>
								<app-input
									(debounce)="vm.setMnrstReceiptNumber($event)"
									label="MNRST Remittance Receipt Number"
									[ngModel]="vm.ltt()?.mnrstReceiptNumber"
									type="string"
									[testHook]="testHooks.MnrstRemittanceReceiptNumber"
								></app-input>

								<p class="font-bold">Municipal non-resident speculation tax</p>
								<p
									class="font-bold text-right"
									[testHook]="testHooks.MNRSTAmount"
								>
									{{ vm.lttCalculated()?.mnrst | centsToDollars }}
								</p>
							</ng-container>
						</ng-container>
					</div>
				</div>
			</ui-section>
		</div>

		<div class="col-span-5">
			<ui-section title="Summary" [testHook]="testHooks.SummarySection">
				<div section-content class="text-gray-700">
					<!-- Prescribed confirmation number -->
					<div class="flex flex-col gap-4 mb-8">
						<app-input
							(debounce)="vm.setConfirmationNumber($event)"
							label="Prescribed confirmation number"
							[ngModel]="vm.ltt()?.confirmationNumber"
							type="string"
							[testHook]="testHooks.PrescribedConfirmationNumber"
						></app-input>
						<a
							href="https://www.teranetexpress.ca/csp/land-transfer-tax-info-login"
							target="_blank"
							[testHook]="testHooks.SetConfirmationNumberLink"
						>
							<ui-button
								label="Go to Teranet Express"
								icon="external-link"
								[testHook]="testHooks.GoToTeranetExpressButton"
							></ui-button>
						</a>
					</div>

					<div class="p-3 bg-grey-lightest rounded-xl mb-5">
						<div class="grid grid-cols-2 p-sm gap-4">
							<p class="h5 col-span-2 text-gray-900">LTT</p>
							<p>Ontario Land transfer tax</p>
							<p class="text-right">
								{{ vm.lttCalculated()?.lttCalculation.tax | centsToDollars }}
							</p>
							<p>Less LTT refund</p>
							<p class="text-right">
								{{ vm.lttCalculated()?.lttCalculation.refund | centsToDollars }}
							</p>
							<p class="font-bold">Net LTT amount payable</p>
							<p class="font-bold text-right">
								{{
									vm.lttCalculated()?.lttCalculation.payable | centsToDollars
								}}
							</p>
						</div>
					</div>

					<div
						class="p-3 bg-grey-lightest rounded-xl mb-5"
						*ngIf="vm.ltt()?.mltt?.applied"
						[@openClose]
					>
						<div class="grid grid-cols-2 gap-3">
							<div>Municipal Land transfer tax</div>
							<div class="text-right">
								{{ vm.lttCalculated()?.mlttCalculation.tax | centsToDollars }}
							</div>
							<div>Less MLTT refund</div>
							<div class="text-right">
								{{
									vm.lttCalculated()?.mlttCalculation.refund | centsToDollars
								}}
							</div>
							<div class="font-bold">Net MLTT amount payable</div>
							<div class="font-bold text-right">
								{{
									vm.lttCalculated()?.mlttCalculation.payable | centsToDollars
								}}
							</div>
						</div>
					</div>

					<div class="grid grid-cols-2">
						<p class="font-bold">Net amount payable</p>
						<p
							class="font-bold text-right"
							[testHook]="testHooks.NetAmountPayable"
						>
							{{ vm.lttCalculated()?.totalPayable | centsToDollars }}
						</p>
					</div>
				</div>
			</ui-section>
		</div>
	</div>
</ui-page>
